<template>
  <header class="hidden md:block">
    <div class="container mx-auto auto-cols-fr grid-cols-3 p-4 md:grid">
      <div class="flex items-center">
        <WordmarkLogo class="h-[80px] max-h-[112px] min-h-[60px] w-auto object-contain" />
      </div>

      <div
        v-if="user?.UserLicensed"
        class="my-2 flex flex-col items-center justify-center space-y-2 rounded-lg text-center"
      >
        <span
          v-if="timerVisible"
          aria-live="polite"
          aria-atomic="true"
          class="mb-1 text-3xl font-extrabold tracking-tight transition-opacity duration-200 lg:text-5xl"
        >
          {{ globals.formatDuration(duration, t) }}
        </span>

        <button
          v-if="highAccessibility"
          @click="timerVisible = !timerVisible"
          :aria-label="
            timerVisible ? t('pages.home.sections.timer.hideTimerLabel') : t('pages.home.sections.timer.showTimerLabel')
          "
          class="rounded-md px-2 py-1 text-sm text-muted-foreground transition-colors hover:text-muted-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
          {{ timerVisible ? t("pages.home.sections.timer.hideTimer") : t("pages.home.sections.timer.showTimer") }}
        </button>

        <span class="text-xs lg:text-sm">
          <template v-if="user?.Name">
            {{ t("pages.home.sections.timer.hi", { name: user.Name }) }}
          </template>
          <i18n-t v-if="isNative" keypath="pages.home.sections.timer.keepAppRunning" tag="span">
            <template #ip><InlineIPAddress :ip="user?.CurrentAddress" /></template>
          </i18n-t>
          <i18n-t v-else keypath="pages.home.sections.timer.keepThisPageOpen" tag="span">
            <template #ip><InlineIPAddress :ip="user?.CurrentAddress" /></template>
          </i18n-t>
        </span>

        <span class="text-xs italic lg:text-sm" v-if="timeUntilUserExpiry">
          {{
            t("pages.home.sections.timer.accountExpiry", {
              days: globals.formatAsDays(timeUntilUserExpiry),
            })
          }}
        </span>
      </div>

      <ul
        class="m-0 flex list-none flex-row-reverse items-center gap-1 p-0 md:gap-2 lg:gap-4"
        role="list"
        aria-label="User actions"
      >
        <li>
          <button
            class="inline-flex h-9 items-center justify-center gap-1 whitespace-nowrap rounded-md bg-destructive px-2 text-xs font-medium text-destructive-foreground ring-offset-background transition-colors hover:bg-destructive/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 md:h-10 md:px-4 md:text-sm lg:h-11 lg:gap-2 lg:px-8"
            @click="handleLogout"
          >
            <ArrowLeftOnRectangleIcon class="h-3 w-3 md:h-4 md:w-4" />
            {{ t("pages.home.header.actions.logout") }}
          </button>
        </li>

        <li v-if="user?.AuthType === 'local'">
          <button
            :aria-label="t('pages.home.header.actions.changePassword')"
            class="inline-flex h-9 w-9 items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 md:h-10 md:w-10 lg:h-11 lg:w-11"
            @click="handleOpenChangePasswordDialog"
          >
            <Cog6ToothIcon class="h-4 w-4 md:h-5 md:w-5" />
          </button>
        </li>

        <li v-if="isNative">
          <UserDiagnosticsExport />
        </li>

        <li>
          <button
            :aria-label="`${t('pages.home.header.actions.colorMode')}: ${getCurrentModeLabel()}`"
            class="inline-flex h-9 w-9 items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 md:h-10 md:w-10 lg:h-11 lg:w-11"
            @click="handleChangeMode"
          >
            <template v-if="mode === 'light'">
              <SunIcon class="h-4 w-4 md:h-5 md:w-5" />
            </template>
            <template v-else-if="mode === 'dark'">
              <MoonIcon class="h-4 w-4 md:h-5 md:w-5" />
            </template>
            <template v-else-if="mode === 'auto'">
              <ComputerDesktopIcon class="h-4 w-4 md:h-5 md:w-5" />
            </template>
          </button>
        </li>

        <li v-if="user?.UserLicensed && !isNative">
          <button
            @click="handleChangeView"
            :aria-label="`${t('pages.home.header.actions.changeView')}: ${getCurrentViewLabel()}`"
            class="inline-flex h-9 w-9 items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 md:h-10 md:w-10 lg:h-11 lg:w-11"
          >
            <CreditCardIcon v-if="view === 'card'" class="h-4 w-4 md:h-5 md:w-5" />
            <TableCellsIcon v-if="view === 'list'" class="h-4 w-4 md:h-5 md:w-5" />
          </button>
        </li>

        <li>
          <AccessMenu :acls="deDuplicatedAcls" />
        </li>
      </ul>
    </div>
  </header>
  <header class="block md:hidden">
    <div class="flex flex-col">
      <div class="container mx-auto flex items-center justify-between p-2">
        <div class="flex min-w-0 flex-shrink-0 items-center">
          <WordmarkLogo class="h-[48px] max-h-[56px] min-h-[40px] w-auto object-contain" />
        </div>

        <ul
          class="m-0 flex list-none flex-row-reverse items-center gap-2 p-0 sm:gap-4"
          role="list"
          aria-label="User actions"
        >
          <li>
            <button
              class="inline-flex h-9 items-center justify-center gap-2 whitespace-nowrap rounded-md bg-destructive px-3 text-sm font-medium text-destructive-foreground ring-offset-background transition-colors hover:bg-destructive/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50"
              @click="handleLogout"
            >
              <ArrowLeftOnRectangleIcon class="h-4 w-4" />
              {{ t("pages.home.header.actions.logout") }}
            </button>
          </li>

          <li v-if="user?.AuthType === 'local'" :class="isNative ? 'hidden landscape:list-item' : ''">
            <button
              :aria-label="t('pages.home.header.actions.changePassword')"
              class="inline-flex h-10 w-10 items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50"
              @click="handleOpenChangePasswordDialog"
            >
              <Cog6ToothIcon class="h-4 w-4" />
            </button>
          </li>

          <li v-if="isNative">
            <UserDiagnosticsExport />
          </li>

          <li>
            <button
              :aria-label="`${t('pages.home.header.actions.colorMode')}: ${getCurrentModeLabel()}`"
              class="inline-flex h-11 w-11 items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50"
              @click="handleChangeMode"
            >
              <template v-if="mode === 'light'">
                <SunIcon class="h-5 w-5" />
              </template>
              <template v-else-if="mode === 'dark'">
                <MoonIcon class="h-5 w-5" />
              </template>
              <template v-else-if="mode === 'auto'">
                <ComputerDesktopIcon class="h-5 w-5" />
              </template>
            </button>
          </li>

          <li v-if="user?.UserLicensed && !isNative">
            <button
              @click="handleChangeView"
              :aria-label="`${t('pages.home.header.actions.changeView')}: ${getCurrentViewLabel()}`"
              class="inline-flex h-11 w-11 items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50"
            >
              <CreditCardIcon v-if="view === 'card'" class="h-5 w-5" />
              <TableCellsIcon v-if="view === 'list'" class="h-5 w-5" />
            </button>
          </li>

          <li>
            <AccessMenu :acls="deDuplicatedAcls" />
          </li>
        </ul>
      </div>

      <div class="my-2 flex flex-col items-center justify-center rounded-lg text-center">
        <template v-if="user?.UserLicensed">
          <span
            v-if="timerVisible && showLiveTimer"
            aria-live="polite"
            aria-atomic="true"
            class="font-mono text-4xl font-extrabold tracking-tight"
          >
            {{ globals.formatDuration(duration, t) }}
          </span>

          <button
            v-if="highAccessibility && showLiveTimer"
            @click="timerVisible = !timerVisible"
            :aria-label="
              timerVisible
                ? t('pages.home.sections.timer.hideTimerLabel')
                : t('pages.home.sections.timer.showTimerLabel')
            "
            class="rounded-md px-2 py-1 text-sm text-muted-foreground transition-colors hover:text-muted-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            {{ timerVisible ? t("pages.home.sections.timer.hideTimer") : t("pages.home.sections.timer.showTimer") }}
          </button>

          <span class="text-sm">
            <i18n-t v-if="isNative" keypath="pages.home.sections.timer.maintainAccessApp" tag="span">
              <template #ip><InlineIPAddress :ip="user?.CurrentAddress" /></template>
            </i18n-t>
            <i18n-t v-else keypath="pages.home.sections.timer.maintainAccessWeb" tag="span">
              <template #ip><InlineIPAddress :ip="user?.CurrentAddress" /></template>
            </i18n-t>
            <template v-if="!showLiveTimer && coarseDurationText">
              {{ " " }}
              {{
                t("pages.home.sections.timer.accessRemaining", {
                  duration: coarseDurationText,
                })
              }}
            </template>
          </span>
          <span class="text-sm italic" v-if="timeUntilUserExpiry">
            {{
              t("pages.home.sections.timer.accountExpiry", {
                days: globals.formatAsDays(timeUntilUserExpiry),
              })
            }}
          </span>
        </template>
      </div>
    </div>
  </header>

  <main v-if="user?.UserLicensed === false" class="mt-20 flex min-h-fit items-center justify-center">
    <div class="mx-auto max-w-md text-center">
      <div class="mb-6">
        <ShieldExclamationIcon
          :class="twMerge('mx-auto h-16 w-16', `text-${theme}${highAccessibility ? '-hc' : ''}`)"
        />
      </div>

      <h1 class="mb-3 text-2xl font-semibold text-foreground">
        {{ t("pages.home.dialogs.unlicensed.userHeading") }}
      </h1>

      <p class="mb-6 leading-relaxed text-secondary-foreground">
        {{ t("pages.home.dialogs.unlicensed.warning") }}
      </p>

      <p class="text-sm text-secondary-foreground">
        {{ t("pages.home.dialogs.unlicensed.contactAdmin") }}
      </p>
    </div>
  </main>

  <main
    v-if="hasNoAccessiblePaths"
    aria-labelledby="no-matching-groups-heading"
    class="mt-20 flex min-h-fit items-center justify-center"
  >
    <div class="mx-auto max-w-md text-center">
      <div class="mb-6">
        <ShieldExclamationIcon
          :class="twMerge('mx-auto h-16 w-16', `text-${theme}${highAccessibility ? '-hc' : ''}`)"
        />
      </div>

      <h1 id="no-matching-groups-heading" class="mb-3 text-2xl font-semibold text-foreground">
        {{ t("pages.home.dialogs.noMatchingGroups.heading") }}
      </h1>

      <p class="mb-6 leading-relaxed text-secondary-foreground">
        {{ t("pages.home.dialogs.noMatchingGroups.message") }}
      </p>

      <template v-if="samlGroups.length > 0">
        <button
          type="button"
          class="text-sm text-muted-foreground underline underline-offset-2 transition-colors hover:text-secondary-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          :aria-expanded="showGroups"
          aria-controls="saml-groups-list"
          @click="showGroups = !showGroups"
        >
          {{
            showGroups
              ? t("pages.home.dialogs.noMatchingGroups.hideGroups")
              : t("pages.home.dialogs.noMatchingGroups.showGroups")
          }}
        </button>

        <div v-if="showGroups" id="saml-groups-list" class="mx-auto mt-4 w-fit text-left">
          <p class="mb-1 text-xs text-muted-foreground">
            {{ t("pages.home.dialogs.noMatchingGroups.groupsIntro") }}
          </p>
          <ul class="list-inside list-disc text-sm text-secondary-foreground">
            <li v-for="group in samlGroups" :key="group">{{ group }}</li>
          </ul>
        </div>
      </template>
    </div>
  </main>

  <main v-if="!hasNoAccessiblePaths && user?.UserLicensed !== false && view === 'list'">
    <div class="mx-auto flex max-w-[1536px] flex-col items-center px-4 py-8">
      <div v-if="deDuplicatedAcls.length > searchThreshold" class="flex w-full justify-center md:w-[50%]">
        <FormKit
          v-model="search"
          :classes="{ outer: 'max-w-full' }"
          prefix-icon="search"
          name="Search"
          placeholder="Search"
          type="text"
        />
      </div>

      <div class="w-full md:w-[75%]">
        <Table class="table-fixed">
          <TableHeader>
            <TableRow>
              <TableHead :class="sortedAcls.some((a) => a.GrantTypeFixedIps) ? 'w-[86px]' : 'w-[48px]'">
                <span class="sr-only">{{ t("pages.home.sections.list.tables.main.columns.Favourite") }}</span>
              </TableHead>
              <TableHead colspan="2">
                {{ t("pages.home.sections.list.tables.main.columns.System") }}
              </TableHead>
              <TableHead class="hidden text-center md:table-cell">
                {{ t("pages.home.sections.list.tables.main.columns.Status") }}
              </TableHead>
              <TableHead class="text-center">
                {{ t("pages.home.sections.list.tables.main.columns.Access") }}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            <template v-for="item in sortedAcls" :key="item.ACLID">
              <ACL
                mode="row"
                :acl="item"
                :child-acl="user?.ACLs?.filter((x) => x.ParentID === item.ACLID)?.[0]"
                @grant-acl="(e) => handleGrantAcl(e, item)"
                @revoke-acl="(e) => handleRevokeAcl(e, item)"
                :is-favourite="favouriteAclIds.includes(item.ACLID)"
                @favourite-acl="(e) => handleFavouriteAcl(e, item)"
              />
            </template>
          </TableBody>
        </Table>
      </div>
    </div>
  </main>

  <main v-if="!hasNoAccessiblePaths && user?.UserLicensed !== false && view === 'card'">
    <ul ref="cardListRef" class="container mx-auto flex flex-col space-y-8 px-4 py-8">
      <li v-if="deDuplicatedAcls.length > searchThreshold" class="flex justify-center">
        <FormKit
          v-model="search"
          :classes="{ outer: 'max-w-full md:max-w-[50%]' }"
          prefix-icon="search"
          name="Search"
          placeholder="Search"
          type="text"
        />
      </li>

      <li v-if="favouriteAcls.filter(searchFilter).length > 0" class="!mt-0 flex flex-col space-y-6">
        <div class="flex items-center space-x-4">
          <h2 class="text-xl font-semibold">
            {{ t("pages.home.sections.favourites.heading") }}
          </h2>

          <div class="grow border-b dark:border-white/30" />
        </div>

        <div class="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4 4xl:grid-cols-5">
          <div v-for="item in favouriteAcls.filter(searchFilter)" :key="item.ACLID">
            <ACL
              mode="card"
              :acl="item"
              :child-acl="user?.ACLs?.filter((x) => x.ParentID === item.ACLID)?.[0]"
              @grant-acl="(e) => handleGrantAcl(e, item)"
              @revoke-acl="(e) => handleRevokeAcl(e, item)"
              :is-favourite="favouriteAclIds.includes(item.ACLID)"
              @favourite-acl="(e) => handleFavouriteAcl(e, item)"
            />
          </div>
        </div>
      </li>

      <li
        v-for="[category, acls] in aclsByCategory.filter(([, acls]) => acls.filter(searchFilter).length > 0)"
        class="flex flex-col space-y-6"
      >
        <div v-if="aclsByCategory.length > 1 || favouriteAcls.length > 0" class="flex items-center space-x-4">
          <h2 class="text-xl font-semibold">
            {{ category === "Default" ? "Other" : category }}
          </h2>

          <div class="grow border-b dark:border-white/30" />
        </div>

        <div class="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4 4xl:grid-cols-5">
          <div v-for="item in acls.filter(searchFilter)" :key="item.ACLID">
            <ACL
              mode="card"
              :acl="item"
              :child-acl="user?.ACLs?.filter((x) => x.ParentID === item.ACLID)?.[0]"
              @grant-acl="(e) => handleGrantAcl(e, item)"
              @revoke-acl="(e) => handleRevokeAcl(e, item)"
              :is-favourite="favouriteAclIds.includes(item.ACLID)"
              @favourite-acl="(e) => handleFavouriteAcl(e, item)"
            />
          </div>
        </div>
      </li>
    </ul>
  </main>

  <Dialog :show="isChangePasswordDialogOpen">
    <DialogOverlay />

    <DialogPanel :theme="theme" :high-contrast="highAccessibility" class="!max-w-[512px]">
      <DialogTitle>
        {{ t("pages.home.dialogs.changePassword.heading") }}
      </DialogTitle>

      <FormKit type="form" :actions="false" @submit="handleSubmitChangePasswordForm">
        <FormKit
          :classes="{ outer: 'max-w-full' }"
          type="password"
          :label="t('pages.home.dialogs.changePassword.forms.changePassword.fields.CurrentPassword.label')"
          name="CurrentPassword"
          validation="required"
          v-bind="
            passwordResetErr
              ? {
                  'aria-invalid': true,
                  'aria-describedby': 'current-password-error',
                }
              : {}
          "
        />

        <div class="mb-4">
          <FormKit
            v-model="newPassword"
            :classes="{ outer: 'max-w-full mb-2' }"
            type="password"
            :label="t('pages.home.dialogs.changePassword.forms.changePassword.fields.NewPassword.label')"
            name="NewPassword"
            validation="required|length:8,64"
          />

          <PasswordMeter :value="newPassword" />
        </div>

        <FormKit
          :classes="{ outer: 'max-w-full' }"
          type="password"
          :label="t('pages.home.dialogs.changePassword.forms.changePassword.fields.ConfirmPassword.label')"
          name="ConfirmPassword"
          validation="required|confirm:NewPassword"
        />

        <div class="flex space-x-3">
          <button
            type="button"
            @click="
              () => {
                isChangePasswordDialogOpen = false;
                passwordResetErr = null;
              }
            "
            class="inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
          >
            {{ t("common.cancel") }}
          </button>

          <button
            type="submit"
            :class="
              twMerge(
                'inline-flex w-full justify-center rounded-md bg-primary px-3 py-2 text-sm font-semibold text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary',
                !highAccessibility &&
                  theme === 'teal' &&
                  'bg-theme-teal hover:bg-theme-teal/90 focus-visible:outline-theme-teal',
                highAccessibility &&
                  theme === 'teal' &&
                  'bg-theme-teal-hc hover:bg-theme-teal-hc/90 focus-visible:outline-theme-teal-hc',
                !highAccessibility &&
                  theme === 'grape' &&
                  'bg-theme-grape hover:bg-theme-grape/90 focus-visible:outline-theme-grape',
                highAccessibility &&
                  theme === 'grape' &&
                  'bg-theme-grape-hc hover:bg-theme-grape-hc/90 focus-visible:outline-theme-grape-hc',
                !highAccessibility &&
                  theme === 'iceberg' &&
                  'bg-theme-iceberg hover:bg-theme-iceberg/90 focus-visible:outline-theme-iceberg',
                highAccessibility &&
                  theme === 'iceberg' &&
                  'bg-theme-iceberg-hc hover:bg-theme-iceberg-hc/90 focus-visible:outline-theme-iceberg-hc',
                !highAccessibility &&
                  theme === 'rose' &&
                  'bg-theme-rose hover:bg-theme-rose/90 focus-visible:outline-theme-rose',
                highAccessibility &&
                  theme === 'rose' &&
                  'bg-theme-rose-hc hover:bg-theme-rose-hc/90 focus-visible:outline-theme-rose-hc',
                !highAccessibility &&
                  theme === 'offsite' &&
                  'bg-theme-offsite hover:bg-theme-offsite/90 focus-visible:outline-theme-offsite',
                highAccessibility &&
                  theme === 'offsite' &&
                  'bg-theme-offsite-hc hover:bg-theme-offsite-hc/90 focus-visible:outline-theme-offsite-hc',
              )
            "
          >
            {{ t("common.submit") }}
          </button>
        </div>

        <p
          v-if="passwordResetErr"
          id="current-password-error"
          class="mt-4 text-sm text-foreground"
          role="alert"
          aria-live="assertive"
        >
          {{ passwordResetErr }}
        </p>
      </FormKit>
    </DialogPanel>
  </Dialog>

  <Dialog :show="isLicensed === false">
    <DialogOverlay />

    <DialogPanel :theme="theme" :high-contrast="highAccessibility" class="!max-w-[512px]">
      <DialogTitle>
        {{ t("pages.home.dialogs.unlicensed.heading") }}
      </DialogTitle>

      <DialogDescription>
        {{ t("pages.home.dialogs.unlicensed.description") }}
      </DialogDescription>

      <div class="flex space-x-3">
        <button
          class="inline-flex w-full justify-center rounded-md bg-primary px-3 py-2 text-sm font-semibold text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary sm:col-start-2"
          @click="handleLogout"
        >
          {{ t("common.logout") }}
        </button>
      </div>
    </DialogPanel>
  </Dialog>

  <RedirectingDialog
    v-if="shouldShowRedirect"
    :seconds="redirectSeconds"
    :to="redirectUrl"
    :external="true"
    @cancel="cancelRedirect"
  />

  <InfoNotification :text="infoMessage" @update:info="(input) => (infoMessage = input)" />

  <ErrorNotification :error="errorMessage" title="Warning" :close="false" />
</template>

<script setup lang="ts">
import { DialogDescription } from "@headlessui/vue";
import {
  ArrowLeftOnRectangleIcon,
  Cog6ToothIcon,
  ComputerDesktopIcon,
  CreditCardIcon,
  MoonIcon,
  SunIcon,
  TableCellsIcon,
} from "@heroicons/vue/24/solid";
import autoAnimate from "@formkit/auto-animate";
import { BasicColorSchema, useColorMode } from "@vueuse/core";
import { isAndroid, isNativePlatform } from "../capacitor/platform";
import { markVpnPermissionDenied } from "../capacitor/vpn-permission";
import { twMerge } from "tailwind-merge";
import { computed, onMounted, onUnmounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";

import globals from "../globals";
import authService from "../services/auth.service.ts";

import ErrorNotification from "../components/ErrorNotification.vue";
import InfoNotification from "../components/InfoNotification.vue";
import PasswordMeter from "../components/PasswordMeter.vue";
import { Dialog, DialogOverlay, DialogPanel, DialogTitle } from "../components/ui/dialog";
import { Table, TableBody, TableHead, TableHeader, TableRow } from "../components/ui/table";
import WordmarkLogo from "../components/WordmarkLogo.vue";
import ACL from "../components/ACL.vue";
import AccessMenu from "../components/AccessMenu.vue";
import InlineIPAddress from "../components/InlineIPAddress.vue";
import { useAuthStore } from "../pinia/auth.ts";
import { useThemeStore } from "../pinia/theme.ts";
import { ShieldExclamationIcon } from "@heroicons/vue/24/outline";
import RedirectingDialog from "../components/RedirectingDialog.vue";
import UserDiagnosticsExport from "../components/UserDiagnosticsExport.vue";
import { getAclStatus } from "../shared/services/aclStatus.ts";
import { grantIps } from "../shared/utils/discovery.ts";

import { isUncategorised, UNCATEGORISED_CATEGORY } from "../utils/knocCategories";
import { useRedirectUrl } from "../utils/useRedirectUrl.ts";
import { useStatusPolling } from "../utils/useStatusPolling.ts";
import { useDiscoveredIps } from "../composables/useDiscoveredIps.ts";
import { performLogout } from "../utils/logoutRedirect";
import { scheduleExpiryNotifications, cancelExpiryNotifications } from "../capacitor/expiry-notifications";

const { t } = useI18n();
const isNative = isNativePlatform();
const isAndroidNative = isAndroid();

// Card list animations are web-only. auto-animate's FLIP transitions mis-measure
// in the native WebView and the cards overlap mid-animation. Attaching then
// disabling proved unreliable (it still animated on tablets), so on native we
// never attach the controller at all (see onMounted).
const cardListRef = ref<HTMLElement | null>(null);

const authStore = useAuthStore();
const themeStore = useThemeStore();
themeStore.fetchTheme();

const user = computed(() => authStore.user);
const theme = computed(() => themeStore.accent);
const highAccessibility = computed(() => themeStore.highAccessibility);
const isLicensed = computed(() => user.value?.Licensed);

const view = ref(localStorage.getItem("view") ? localStorage.getItem("view") : "card");

function handleChangeView() {
  if (view.value === "card") {
    view.value = "list";
    localStorage.setItem("view", "list");
    return;
  }

  view.value = "card";
  localStorage.setItem("view", "card");
}

const router = useRouter();
const route = useRoute();

const duration = ref(globals.timeRemaining(user.value?.Ends));
const timeUntilUserExpiry = ref(user.value?.ValidUntil);

// On the mobile dashboard, the live HH:MM:SS countdown is hidden whenever
// access has more than this much time remaining; the description below
// shows a coarser "more than {N} days/hours" string instead. Falls back to
// the live timer once we drop into the last few hours, where the user
// actually benefits from second-level precision.
const SHOW_TIMER_THRESHOLD_MS = 6 * 60 * 60 * 1000;
const showLiveTimer = computed(() => duration.value > 0 && duration.value < SHOW_TIMER_THRESHOLD_MS);
const coarseDurationText = computed(() => globals.formatCoarseDuration(duration.value, t));

const infoMessage = ref("");
const errorMessage = ref("");

const timerVisible = ref(true);

const passwordResetErr = ref(null);

const hasUnstableAcls = computed(() => !user.value?.ACLs?.every(isGrantInStableState));

// Native apps have less horizontal room, so surface search sooner.
const searchThreshold = isNative ? 7 : 10;

const favouriteAclIds = ref(
  (() => {
    try {
      const stored = localStorage.getItem("favouriteAclIds");
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  })(),
);
watch(
  favouriteAclIds,
  (value) => {
    localStorage.setItem("favouriteAclIds", JSON.stringify(value));
  },
  { deep: true },
);

const deDuplicatedAcls = computed(() => {
  if (!user.value?.ACLs) return [];

  let uniqueAcls = [];
  user.value.ACLs.filter((acl) => {
    if (acl.HideFromUsers) {
      return false;
    }
    if (!!acl.ParentID) {
      return false;
    }
    return true;
  }).forEach((acl) => {
    if (!uniqueAcls.find((a) => a.Description == acl.Description)) {
      uniqueAcls.push(acl);
    }
  });

  return uniqueAcls;
});

const samlGroups = computed(() => user.value?.SamlGroups ?? []);
const showGroups = ref(false);

// SAML user whose assertion matched no Knoc-granting group. The array check waits for the first status fetch.
const hasNoAccessiblePaths = computed(
  () =>
    user.value?.AuthType === "saml" &&
    user.value?.UserLicensed !== false &&
    Array.isArray(user.value?.ACLs) &&
    user.value.ACLs.length === 0,
);

const aclsByCategory = computed(() => {
  const defaultCategory = UNCATEGORISED_CATEGORY;
  return Object.entries(
    deDuplicatedAcls.value
      .filter((acl) => !favouriteAclIds.value.includes(acl.ACLID))
      .reduce((acc, acl) => {
        // The shared rule, so a padded or legacy "Default" row does not get a
        // group of its own that looks identical to the real one.
        const category = isUncategorised(acl.Category) ? defaultCategory : acl.Category.trim();
        if (!acc[category]) acc[category] = [];
        acc[category].push(acl);
        return acc;
      }, {}),
  ).sort((a, b) => {
    if (a[0] === defaultCategory) return 1;
    if (b[0] === defaultCategory) return -1;
    return a[0].localeCompare(b[0]);
  });
});

const { redirectUrl, redirectSeconds, shouldShowRedirect, cancelRedirect } = useRedirectUrl(deDuplicatedAcls);

const favouriteAcls = computed(() => {
  return favouriteAclIds.value.map((id) => deDuplicatedAcls.value.find((acl) => acl.ACLID === id)).filter(Boolean);
});

const sortedAcls = computed(() => {
  return deDuplicatedAcls.value.filter(searchFilter).sort((a, b) => {
    const aIsFavourite = favouriteAclIds.value.includes(a.ACLID);
    const bIsFavourite = favouriteAclIds.value.includes(b.ACLID);
    if (aIsFavourite && !bIsFavourite) return -1;
    if (!aIsFavourite && bIsFavourite) return 1;
    return a.Description.localeCompare(b.Description);
  });
});

const isChangePasswordDialogOpen = ref(false);
const newPassword = ref(null);

function handleOpenChangePasswordDialog() {
  newPassword.value = "";
  isChangePasswordDialogOpen.value = true;
}

const search = ref("");

function searchFilter(item) {
  return (
    search.value === "" ||
    (item.Description ?? "").toLowerCase().includes(search.value.toLowerCase()) ||
    (item.URL ?? "").toLowerCase().includes(search.value.toLowerCase()) ||
    (item.Icon ?? "").toLowerCase().includes(search.value.toLowerCase())
  );
}

const mode = useColorMode({
  initialValue: isNativePlatform() ? "auto" : "dark",
  emitAuto: true,
});

function handleChangeMode() {
  const modes: BasicColorSchema[] = ["light", "dark", "auto"];

  const currentIndex = modes.indexOf(mode.value);
  const nextIndex = (currentIndex + 1) % modes.length;
  mode.value = modes[nextIndex];
}

function handleFavouriteAcl(e, acl) {
  e.preventDefault();

  if (favouriteAclIds.value.includes(acl.ACLID)) {
    favouriteAclIds.value = favouriteAclIds.value.filter((id) => id !== acl.ACLID);
  } else {
    favouriteAclIds.value = [...favouriteAclIds.value, acl.ACLID];
  }
}

async function handleSubmitChangePasswordForm(data) {
  try {
    await authService.changePassword(data);
    isChangePasswordDialogOpen.value = false;
    infoMessage.value = t("msg.passwordChangedSuccessfully");
    passwordResetErr.value = null;
  } catch (err) {
    passwordResetErr.value = globals.renderError(t, err);
  }
}

async function handleLogout() {
  try {
    await cancelExpiryNotifications();
    if (isNative) {
      const { default: IpChange } = await import("../capacitor/ip-change-plugin");
      await IpChange.setSessionActive({ active: false });
    }
  } catch {
    // Best-effort cleanup; performLogout still runs.
  }
  await performLogout();
}

let countdown: NodeJS.Timeout;

let grantRevokeIntervals: Record<string, NodeJS.Timeout> = {};

function isGrantInStableState(acl) {
  const status = getAclStatus(acl.Status);
  if (!status) {
    // Couldn't find the status, treat it as stable for now
    return true;
  }
  if (status.unstable) {
    return false;
  }
  // Delegated/agentic device grants transition independently of the owner's
  // own status; an in-flight device must keep the faster poll tiers going.
  return !(acl.PersistentIPs ?? []).some((d) => getAclStatus(d.Status)?.unstable ?? false);
}

// CGNAT IP discovery: owns its own poll cadence, the port walk, submission, and
// the cached set used by click-to-grant. Decoupled from status polling.
const { ips: discoveredIps, enabled: discoveryEnabled } = useDiscoveredIps({
  currentAddress: computed(() => user.value?.CurrentAddress),
  hasCgnatOpenKnoc: () => !!user.value?.ACLs?.some((acl) => acl.CGNATOpen),
});

// Set up status polling with three-tier intervals.
// burstPoll fetches every 1s for the first 10s after mount to catch an
// auto-grant landing just after the mount fetch, before hasUnstableAcls
// would otherwise flip to aggressive polling.
const { triggerAggressivePolling } = useStatusPolling({
  fetchStatus: async () => {
    const user = await authStore.fetchStatus({ force: true });
    if (user) scheduleExpiryNotifications(user);
    return user;
  },
  hasUnstableAcls,
  burstPoll: { intervalMs: 1000, durationMs: 10_000 },
});

async function handleGrantAcl(e, acl) {
  e.preventDefault();

  // For a CGNATOpen knoc, attach the IPs already found by the background
  // discovery poll so they go live with this grant. These are cached results;
  // clicking does not probe. IPs discovered after this click are picked up by
  // the poll.
  const ips = grantIps(user.value?.CurrentAddress, acl.CGNATOpen, discoveryEnabled.value, discoveredIps.value);
  try {
    await authService.grantAcl(acl.ACLID, ips, acl.TicketNumber, acl.TOTPCode);
  } finally {
    // Refresh after both success and failure: on a server-side reject (e.g.
    // GreyNoise block) the new blocked Grant row is what flips the UI out of
    // "Grant Access" into the blocked state.
    await authStore.fetchStatus({ force: true });
    triggerAggressivePolling();
  }
}

async function handleRevokeAcl(e, acl) {
  e.preventDefault();

  await authService.revokeAcl(acl.ACLID);
  await authStore.fetchStatus({ force: true });

  triggerAggressivePolling();
}

function getCurrentModeLabel() {
  const labels = {
    light: t("pages.home.header.actions.lightMode"),
    dark: t("pages.home.header.actions.darkMode"),
    auto: t("pages.home.header.actions.systemMode"),
  };
  return labels[mode.value] || mode.value;
}

function getCurrentViewLabel() {
  const labels = {
    card: t("pages.home.header.actions.cardView"),
    list: t("pages.home.header.actions.listView"),
  };
  return labels[view.value] || view.value;
}

onMounted(async () => {
  // Attach card animations on web only; never on native (WebView FLIP overlap).
  if (!isNative && cardListRef.value) {
    autoAnimate(cardListRef.value);
  }

  if (isNative) {
    import("../capacitor/ip-change-plugin").then(({ default: IpChange }) => {
      IpChange.setSessionActive({ active: true });
      IpChange.addListener("vpnPermissionDenied", () => {
        markVpnPermissionDenied();
      });
    });
  }

  countdown = setInterval(async () => {
    duration.value = globals.timeRemaining(user.value.Ends);

    if (duration.value <= 0) {
      let lUser = null;
      try {
        lUser = await authStore.fetchStatus({ force: true });
      } catch (err) {
        // Don't do anything, but this will throw an error if not authorised
      }

      let newTimeRemaining = lUser == null ? 0 : globals.timeRemaining(lUser.Ends);
      if (newTimeRemaining > 0) {
        duration.value = newTimeRemaining;
        return;
      }

      await router.push({ name: "Login", query: route.query });

      clearInterval(countdown);
    }
  }, 1000);

  await authStore.fetchStatus({ force: true });
});

onUnmounted(() => {
  clearInterval(countdown);

  for (const interval of Object.values(grantRevokeIntervals)) {
    clearInterval(interval);
  }
});
</script>

<style scoped>
.text-teal {
  @apply text-theme-teal;
}

.text-teal-hc {
  @apply text-theme-teal-hc;
}

.text-grape {
  @apply text-theme-grape;
}

.text-grape-hc {
  @apply text-theme-grape-hc;
}

.text-iceberg {
  @apply text-theme-iceberg;
}

.text-iceberg-hc {
  @apply text-theme-iceberg-hc;
}

.text-rose {
  @apply text-theme-rose;
}

.text-rose-hc {
  @apply text-theme-rose-hc;
}

.text-offsite {
  @apply text-theme-offsite;
}

.text-offsite-hc {
  @apply text-theme-offsite-hc;
}

.text-default {
  @apply text-theme-grape;
}
</style>
